import Flex from "@ui-kit/layout/Flex/Flex.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useRouter } from "next/router";
import { Dispatch, SetStateAction, useState } from "react";
import SwiperCore, { Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.min.css";
import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import { Tabs } from "@/components/atoms/Tabs";
import useMediaQuery from "@/hooks/useMediaQuery";
import { breakpoints } from "@/styles/constants";
import { TabOption, useSyncTabWithScroll } from "./helpers";

const { xs, sm, md, lg } = breakpoints;

// Swiper container styles (CSS-in-JS for Swiper's class-based styling)
const swiperContainerStyles = {
  "& .swiper-slide": {
    width: "fit-content",
    minWidth: "fit-content",
    maxWidth: "fit-content",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    marginLeft: "4px",
  },
  "& .swiper-container": {
    marginLeft: "0 !important",
    marginRight: "0 !important",
    width: "100%",
  },
  "& .swiper-button-disabled": {
    display: "none",
  },
};

interface TabButtonProps {
  isSelected: boolean;
  onClick: () => void;
  title: string;
}

const TabButton = ({ isSelected, onClick, title }: TabButtonProps) => (
  <Box
    as="button"
    role="button"
    tabIndex={0}
    onClick={onClick}
    data-testid={`${title}-tab`}
    bg="transparent"
    border="none"
    p="none"
    pb="sm"
    mx="md"
    fontWeight="normal"
    cursor="pointer"
    borderBottom={isSelected ? "3px solid" : "3px solid transparent"}
    borderBottomColor={isSelected ? "primary.fg" : "transparent"}
    _hover={{
      bg: "transparent",
    }}
    _focusVisible={{
      outline: "2px solid",
      outlineColor: "blue.focusRing",
      borderRadius: "1px",
    }}
    // eslint-disable-next-line showpass-custom/prefer-semantic-props
    css={{
      [`@media (min-width: ${sm}px) and (max-width: ${lg - 1}px)`]: {
        "&:first-of-type": {
          marginLeft: 0,
        },
      },
    }}
  >
    <Text fontSize={{ base: "xs", sm: "sm" }} color="fg">
      {title}
    </Text>
  </Box>
);

interface HeaderTabsProps {
  tabOptionsList: TabOption[];
  selectedTab: number;
  setSelectedTab: Dispatch<SetStateAction<number>>;
  /** When true, scroll-based tab sync is disabled (e.g., during URL navigation) */
  scrollSyncDisabled?: boolean;
}

const HeaderTabs = ({
  tabOptionsList,
  selectedTab,
  setSelectedTab,
  scrollSyncDisabled = false,
}: HeaderTabsProps): JSX.Element => {
  const router = useRouter();
  const [isMobile] = useMediaQuery(`(max-width: ${xs - 1}px)`);
  const [isTablet] = useMediaQuery(
    `(min-width: ${xs}px) and (max-width: ${md - 1}px)`
  );

  SwiperCore.use([Navigation]);
  const [swiper, setSwiper] = useState<SwiperCore | undefined>();
  const swiperBeginning = 0;

  const visibleTabs = tabOptionsList.filter((tab) => tab.showTab);

  const handleTabClick = (tab: TabOption, index: number) => {
    const tabInfo = visibleTabs.find((tabOption) => tabOption.id === tab.id);

    if (tabInfo?.hash) {
      // Don't set selected tab here - let scroll sync handle it once scroll completes
      router.replace(
        {
          hash: tabInfo?.hash,
        },
        undefined,
        { shallow: true }
      );

      if ((isMobile || isTablet) && visibleTabs.length > 2) {
        if (swiper && index === 1) {
          swiper.slideTo(swiperBeginning);
        } else if (swiper && index >= 2) {
          const tabToSwipeTo = 2;
          swiper.slideTo(index - tabToSwipeTo);
        }
      }
    } else if (tabInfo?.openModal) {
      tabInfo.openModal();
    }
  };

  useSyncTabWithScroll({
    visibleTabs,
    selectedTab,
    swiper,
    setSelectedTab,
    disabled: scrollSyncDisabled,
  });

  return (
    <Flex bg="bg" w="100%">
      <Tabs
        width="100%"
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        isManual
        index={selectedTab}
        pl={{ sm: "2xl" }}
        css={swiperContainerStyles}
      >
        <IconButton
          iconName="ChevronLeft"
          iconOutline={false}
          iconSize="20px"
          aria-label="Previous tab"
          className="prev-button"
          iconColor="fg"
          marginLeft={{ base: "none", sm: "-10px" }}
          data-testid="prev-nav-button"
          display={swiper ? undefined : "none"}
          _disabled={{
            display: "none",
          }}
        />
        <Swiper
          navigation={{
            prevEl: ".prev-button",
            nextEl: ".next-button",
          }}
          allowTouchMove={visibleTabs.length > 3}
          slidesPerView="auto"
          onSwiper={setSwiper}
          initialSlide={selectedTab === 0 ? 0 : selectedTab}
          watchOverflow
        >
          {visibleTabs.map((tab: TabOption, index: number) => (
            <SwiperSlide key={tab.title}>
              <Flex alignItems="flex-end" h="element.4xl">
                <TabButton
                  isSelected={selectedTab === tab.id}
                  onClick={() => handleTabClick(tab, index)}
                  title={tab.title ?? ""}
                />
              </Flex>
            </SwiperSlide>
          ))}
        </Swiper>
        <IconButton
          iconName="ChevronRight"
          iconOutline={false}
          iconSize="20px"
          aria-label="Next tab"
          iconColor="fg"
          className="next-button"
          data-testid="next-nav-button"
          display={swiper ? undefined : "none"}
          _disabled={{
            display: "none",
          }}
        />
      </Tabs>
    </Flex>
  );
};

export default HeaderTabs;
